import React, {useEffect} from 'react';

import {withRouter, WithRouterProps} from 'react-router';

import {useAcceptance} from 'app/acceptance/api';
import {translateAcceptanceContentType} from 'app/acceptance/types';
import {useAddComment} from 'app/comments/api';
import {CommentFormWithUploader} from 'app/comments/components/CommentFormWithUploader';
import {CloseURLSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import {getObjectAcceptURL, getObjectCurrentVersionURL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {AcceptTable} from './AcceptTable';

import {AttachedCommentImage} from 'sweet-types';

import type {
  BaseDocumentContentType,
  BasicUserSerializedType,
  CommentSerializedType,
} from 'sweet-types';

interface Props extends WithRouterProps {
  documentId: number;
  documentType: BaseDocumentContentType;
  documentHtmlURL: string;
  versionURL?: string;
  versionApprovedAt: string | null;
  versionApprovedBy: BasicUserSerializedType | null;
  userId?: number;
  nextReviewDate: string | null;
}

export const GenericAcceptSidebar = withRouter(
  ({
    documentId,
    documentType,
    documentHtmlURL,
    versionURL,
    versionApprovedAt,
    versionApprovedBy,
    userId,
    location,
    router,
    nextReviewDate,
  }: Props) => {
    const [addComment] = useAddComment();
    const acceptance = useAcceptance({
      object_pk: documentId,
      content_type: documentType,
    }).data?.results?.[0];
    useEffect(() => {
      if (acceptance && acceptance.id) {
        const versionHtmlURL = getObjectCurrentVersionURL(
          undefined,
          acceptance.object_id,
          '',
          documentHtmlURL
        );
        router.push(
          getObjectAcceptURL(
            undefined,
            acceptance.object_id,
            location.search,
            versionHtmlURL
          )
        );
      }
    }, [acceptance && acceptance.id]);

    return (
      <CloseURLSidebarPagePanels
        title={gt('Approval Requested')}
        closeUrl={documentHtmlURL}
        footer={
          acceptance != null ? (
            <CommentFormWithUploader
              className="d-flex justify-content-between"
              buttonClass="btn-primary"
              onSubmit={(
                comment: string,
                attachments: Array<AttachedCommentImage>
              ) =>
                addComment({
                  object_pk: acceptance.object_id,
                  parentId: documentId,
                  content_type: acceptance.content_type,
                  comment: comment,
                  attachments: {images: attachments},
                })
                  .unwrap()
                  .then((result: CommentSerializedType) => {
                    // append newly added comments id to url for easy highlighting
                    const {pathname} = location;
                    router.replace({
                      pathname,
                      hash: `#comment${result?.id}`,
                    });
                  })
              }
              contentType={translateAcceptanceContentType(
                acceptance.content_type
              )}
              hideTooltip={true}
              objectId={documentId}
            />
          ) : null
        }
      >
        {versionURL != null && userId != null && acceptance != null ? (
          <AcceptTable
            acceptance={acceptance}
            documentId={documentId}
            documentType={documentType}
            documentHtmlURL={documentHtmlURL}
            versionURL={versionURL}
            versionApprovedAt={versionApprovedAt}
            versionApprovedBy={versionApprovedBy}
            userId={userId}
            nextReviewDate={nextReviewDate}
          />
        ) : null}
      </CloseURLSidebarPagePanels>
    );
  }
);
